import {Text} from '@rdcomponents/text/text';
import {Button, TableColumnsType} from 'antd';
import {decamelize} from 'humps';

import {appRoute} from '../../../../../app-route';
import {EmptyCell} from '../../../../../layout/table/empty-cell/empty-cell';
import {FormattedNumber} from '../../../../../provider/locale/formatted-number';
import {Locale} from '../../../../../provider/locale/locale';
import {compileMainFilterApi} from '../../../../../service/main-filter/main-filter-api';
import {MainFilterSelectedValuesIdsKeyEnum} from '../../../../../service/main-filter/main-filter-api-type';
import {GroupOperationsEnum} from '../../../../../service/main-filter/main-filter-type';
import {
    ReviewAnalysisCitiesSortKeyType,
    ReviewCityType,
} from '../../../../../service/reivew-analysis/review-cities/review-cities-type';
import {ReviewsAndAnswersStatisticsUnitMeasurementEnum} from '../../../../../service/reviews-and-answers-statistics/reviews-and-answers-statistics';
import {
    getAvgResponseDelayColumn,
    getSentimentColumn,
    getTrendColumn,
} from '../../components/table/reviews-analysis-table-helper';

import {emptyReviewCity} from './cities-page-const';
import {CityTableDataType} from './cities-page-type';

export function citiesPageColumns({
    timeUnit,
    mainPeriod,
    getSortOrder,
}: {
    timeUnit: ReviewsAndAnswersStatisticsUnitMeasurementEnum;
    mainPeriod?: string;
    getSortOrder?: (columnKey: ReviewAnalysisCitiesSortKeyType) => 'ascend' | 'descend' | undefined | null;
}): TableColumnsType<CityTableDataType> {
    return [
        {
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__CITY" />,
            dataIndex: 'cityName',
            key: 'cityName',
            render: function renderCityName(_value: unknown, record): JSX.Element {
                async function handleClick() {
                    try {
                        const result = await compileMainFilterApi({
                            [MainFilterSelectedValuesIdsKeyEnum.BRANDS]: [],
                            [MainFilterSelectedValuesIdsKeyEnum.CITIES]: [record.cityId.toString()],
                            [MainFilterSelectedValuesIdsKeyEnum.STREETS]: [],
                            [MainFilterSelectedValuesIdsKeyEnum.CODES]: [],
                            [MainFilterSelectedValuesIdsKeyEnum.NAMES]: [],
                            [MainFilterSelectedValuesIdsKeyEnum.COUNTRIES]: [],
                            [MainFilterSelectedValuesIdsKeyEnum.REGIONS]: [],
                            [MainFilterSelectedValuesIdsKeyEnum.GROUPS]: [],
                            [MainFilterSelectedValuesIdsKeyEnum.GROUPS_LOGICAL_OPERATION]: GroupOperationsEnum.AND,
                        });

                        window.location.href = `${appRoute.reviewsManagementReviews.path}?ordering=-creation_date&filter-id=${result.key}`;
                    } catch (error) {
                        console.log(error);
                    }
                }

                return record.isCompareItem ? (
                    <Text block secondary>
                        {record.cityName}
                    </Text>
                ) : (
                    <Text block>
                        <Button onClick={handleClick} style={{padding: 0}} type="link">
                            {record.cityName}
                        </Button>
                        {mainPeriod && (
                            <Text
                                block
                                secondary
                                stringKey="REVIEWS_ANALYSIS__DASHBOARD__COMPARE_PERIOD_NAME"
                                valueMap={{
                                    period: mainPeriod,
                                }}
                            >
                                {mainPeriod}
                            </Text>
                        )}
                    </Text>
                );
            },
        },
        {
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__RATE" />,
            dataIndex: 'avgRating',
            align: 'center',
            key: 'avgRating',
            render: function renderRate(_value: unknown, record): JSX.Element {
                if (!record.avgRating) {
                    return <EmptyCell />;
                }

                return <span>{record.avgRating.toFixed(1)}</span>;
            },
            sorter: Boolean(getSortOrder),
            sortOrder: getSortOrder?.('avgRating'),
        },
        {
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__REVIEW_TOTAL" />,
            dataIndex: 'total',
            align: 'center',
            key: 'total',
            render: function renderTotal(_value: unknown, record): JSX.Element {
                if (!record.total) {
                    return <EmptyCell />;
                }

                return <FormattedNumber value={record.total} />;
            },
            sorter: Boolean(getSortOrder),
            sortOrder: getSortOrder?.('total'),
        },
        {
            dataIndex: 'replied',
            align: 'center',
            key: 'replied',
            render: function renderReplied(_value: unknown, record): JSX.Element {
                if (!record.replied) {
                    return <EmptyCell />;
                }

                return <FormattedNumber value={record.replied} />;
            },
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__REVIEWS_REPLIED" />,
            sorter: Boolean(getSortOrder),
            sortOrder: getSortOrder?.('replied'),
        },
        {
            dataIndex: 'repliedPercent',
            key: 'repliedPercent',
            render: function renderRepliedPercentage(_value: unknown, record: {repliedPercent: number}): JSX.Element {
                if (!record.repliedPercent) {
                    return <EmptyCell />;
                }

                return <FormattedNumber options={{style: 'percent'}} value={record.repliedPercent} />;
            },
            title: <Locale stringKey="REVIEWS_ANALYSIS__TABLE__COLUMN__WITH_MESSAGE_PERCENTAGE" />,
            sorter: Boolean(getSortOrder),
            sortOrder: getSortOrder?.('repliedPercent'),
        },
        {
            ...getAvgResponseDelayColumn<CityTableDataType>(timeUnit),
            sorter: Boolean(getSortOrder),
            sortOrder: getSortOrder?.('avgResponseSpeed'),
        },
        getSentimentColumn<CityTableDataType>(),
        getTrendColumn<CityTableDataType>(),
    ];
}

export function mergeCompareData(
    data: Array<ReviewCityType>,
    compareData: Array<ReviewCityType>,
    compareName: string
): Array<CityTableDataType> {
    return data.flatMap((dataItem: ReviewCityType, index: number): Array<CityTableDataType> => {
        const compareDataItem: ReviewCityType =
            compareData.find(({cityId}: ReviewCityType): boolean => cityId === dataItem.cityId) || emptyReviewCity;

        return [
            {
                ...dataItem,
                key: dataItem.cityId,
                isCompareItem: false,
                compareItem: compareDataItem,
            },
            {
                ...compareDataItem,
                key: `${compareDataItem.cityId || 0}-${index}`,
                cityName: compareName,
                isCompareItem: true,
                compareItem: dataItem,
            },
        ];
    });
}

export function mergeCompetitorCompareData(
    data: Array<{clientInfo: ReviewCityType | null; competitorInfo: ReviewCityType | null}>,
    compareName: string
): Array<CityTableDataType> {
    return data.flatMap((dataItem, index: number): Array<CityTableDataType> => {
        const clientData = dataItem.clientInfo || emptyReviewCity;
        const competitorData = dataItem.competitorInfo || emptyReviewCity;

        return [
            {
                ...clientData,
                key: clientData.cityId || index,
                cityName: clientData.cityName || competitorData.cityName,
                isCompareItem: false,
                compareItem: competitorData,
            },
            {
                ...competitorData,
                key: `${competitorData.cityId || 0}-${index}`,
                cityName: compareName,
                isCompareItem: true,
                compareItem: clientData,
            },
        ];
    });
}

export function getCitiesTableSorterQueryValue(columnKey: string, sortOrder: 'ascend' | 'descend' | null): string {
    return `${decamelize(columnKey)}.${sortOrder === 'ascend' ? 'asc' : 'desc'}`;
}
